import { isPlatformBrowser } from '@angular/common';
import { Component, HostBinding, inject, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import {
    ApiService,
    AppState,
    ConfigService,
    PageService,
    type PageType,
    pickContrastingTextColor,
    TranslateService,
    WindowService,
    ZoneService,
    type ZoneType,
} from '@gallaghercp/core-frontend';
import { Store } from '@ngrx/store';
import { TranslateService as TranslateProvider } from '@ngx-translate/core';
import { Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';

@Component({
    selector: 'core-footer',
    templateUrl: './footer.component.html',
    standalone: false,
})
export class FooterComponent implements OnInit, OnDestroy {
    private pages = inject(PageService);
    private api = inject(ApiService);
    protected translate = inject(TranslateService);
    protected zoneService = inject(ZoneService);
    protected router = inject(Router);
    protected windowService = inject(WindowService);
    private platformId = inject(PLATFORM_ID);
    protected config = inject(ConfigService);
    private store = inject<Store<AppState>>(Store);
    isBrowser: boolean = isPlatformBrowser(this.platformId);

    @HostBinding('style.display') displayStyle: string = 'block';
    @HostBinding('class.footer--dark-bg') isDarkBg: boolean = false;
    @HostBinding('style.--footer-bg') bgFooter?: string;

    @HostBinding('style.--color-footer-bg') get colorFooterBg(): string | undefined {
        return this.bgFooter;
    }

    @HostBinding('style.--footer-link-color') footerLinkColor?: string;
    @HostBinding('style.--footer-text') footerTextColor?: string;
    @HostBinding('style.--footer-border-top') footerBorderTop?: string;

    @HostBinding('style.--color-footer-text') get colorFooterText(): string | undefined {
        return this.footerTextColor;
    }

    copyright: string | undefined = this.config.get('copyright');
    year: number = new Date().getFullYear();

    pages$?: Observable<any>;
    subscription: Subscription = new Subscription();
    translateProvider: TranslateProvider = inject(TranslateProvider);
    cookiePolicy?: string = this.config.get('cookiePolicy');
    cookiePolicyUsCompliance: boolean = !!this.config.get('cookiePolicyUsCompliance');
    isFooterBar?: boolean = this.config.get('footerBar') ?? true;
    disclaimer?: string = this.config.get('footer')?.disclaimer;
    footerStyle?: string = this.config.get('footer')?.style ?? 'legacy';
    contactsText?: string = (this.config.get('footer') as any)?.contacts ?? '';
    linkDividerCharacter?: string = (this.config.get('footer') as any)?.linkDividerCharacter ?? '';
    isSplashLandingConfigEnabled = !!this.config.get('splashLandingPage');
    isSplashLandingCurrentPage: boolean = false;
    isSplashLandingFooterLinks = this.config.get('splashLandingPage')
        ? ((this.config.get('splashLandingPage') as any)['isFooterLinks'] ?? true)
        : true;
    isLoginPageFooterLinks = this.config.get('loginPage')
        ? ((this.config.get('loginPage') as any)['isFooterLinks'] ?? true)
        : true;
    hideFooterLinksOnSplashLanding: boolean = false;
    isZoneRouteOne = this.config.get('splashLandingPage')
        ? (this.config.get('splashLandingPage') as any)['isZoneRouteOne'] !== undefined
            ? (this.config.get('splashLandingPage') as any)['isZoneRouteOne']
            : true
        : false;
    isFooterButtonBackToSplashPageEnabled = this.config.get('splashLandingPage')
        ? (this.config.get('splashLandingPage') as any)['isFooterButtonBackToSplashPageEnabled']
        : false;
    showFooterButtonBackToSplashPage: boolean = false;
    currentZoneName: string = '';
    isLoginPage: boolean = false;
    zoneSelectionButton?: any | undefined = (this.config.get('footer') as any)?.zoneSelectionButton ?? undefined;

    get zoneSelectionButtonPadding(): string | undefined {
        return this.zoneSelectionButton?.padding;
    }

    get zoneSelectionButtonBorderRadius(): string | undefined {
        return this.zoneSelectionButton?.borderRadius;
    }

    get zoneSelectionButtonTitleTransform(): string {
        const casing = this.zoneSelectionButton?.titleCase ?? this.zoneSelectionButton?.titleTransform;
        if (!casing) {
            return 'uppercase';
        }
        if (casing === 'none') {
            return 'none';
        }
        if (casing === 'capitalized' || casing === 'capitalize') {
            return 'capitalize';
        }
        if (casing === 'allCaps' || casing === 'all-caps' || casing === 'uppercase') {
            return 'uppercase';
        }
        return casing;
    }

    get hideSelectTextOnDesktop(): boolean {
        return !!(this.zoneSelectionButton?.hideSelectTextOnDesktop ?? this.zoneSelectionButton?.hideSelectText);
    }

    ngOnInit() {
        this.updateFooterColors();

        this.subscription.add(
            this.store
                .select(state => state.app)
                .subscribe(() => {
                    this.updateFooterColors();
                }),
        );
        this.subscription.add(
            this.api.zoneChangeEmit.subscribe((zone: number | false) => {
                this.getPages(zone);
                this.updateZoneName();
            }),
        );

        this.subscription.add(
            this.translateProvider.onLangChange.subscribe(() => {
                this.getPages();
                this.updateZoneName();
            }),
        );

        // Footer logo selection is now handled by <core-brand-logo> contrast logic.

        if (this.isSplashLandingConfigEnabled || this.config.get('loginPage')) {
            // Only get's run if the splashLandingPage or loginPage object is present in app.config
            this.updateSplashLandingFooterState();
        }

        this.updateZoneName();

        this.subscription.add(
            this.router.events.subscribe((event: any) => {
                if (event instanceof NavigationEnd) {
                    if (this.isSplashLandingConfigEnabled || this.config.get('loginPage')) {
                        this.updateSplashLandingFooterState();
                    }
                }
            }),
        );
    }

    ngOnDestroy() {
        this.subscription.unsubscribe();
    }

    // Footer no longer computes a logo URL; BrandLogoComponent selects by contrast.

    private getPages(zone?: number | false): void {
        const headers: any = zone ? { 'x-service-zone': String(zone) } : {};
        this.pages$ = this.pages.getPages(headers).pipe(
            map((pages: PageType[]) =>
                pages.map((page: PageType) => ({
                    ...page,
                    name: this.translate.getPageSpecific(page, 'name'),
                    description: this.translate.getPageSpecific(page, 'description'),
                    url: this.zoneService.injectZoneCodeUrl(this.translate.getPageSpecific(page, 'url')),
                    tags: this.translate.getPageSpecific(page, 'tags'),
                })),
            ),
        );
    }

    private updateSplashLandingFooterState(): void {
        const isHome =
            this.router.url === '/' || (this.isBrowser && this.windowService.nativeWindow?.location.pathname === '/');
        this.isLoginPage = this.router.url.includes('/login');
        this.isSplashLandingCurrentPage = isHome && this.isZoneRouteOne;

        if (this.isSplashLandingCurrentPage) {
            this.hideFooterLinksOnSplashLanding = !this.isSplashLandingFooterLinks;
        } else if (this.isLoginPage) {
            this.hideFooterLinksOnSplashLanding = !this.isLoginPageFooterLinks;
        } else {
            this.hideFooterLinksOnSplashLanding = false;
        }

        this.showFooterButtonBackToSplashPage =
            this.isFooterButtonBackToSplashPageEnabled && !this.isSplashLandingCurrentPage && !this.isLoginPage;
    }

    private updateZoneName(): void {
        const currentZone = this.zoneService.getCurrentZone() as ZoneType;
        this.currentZoneName = currentZone?.name || '';
    }

    protected navigateToSplashPage() {
        this.api.setServiceZone(1);
        this.router.navigate(['/']);
    }

    private updateFooterColors(): void {
        const configColors = this.config.get('colors') as Record<string, any> | undefined;
        const footerBg: string | undefined =
            configColors?.['footerBg'] ?? configColors?.['elementBackgroundColour'] ?? configColors?.['secondary2'];

        this.bgFooter = footerBg;
        this.footerBorderTop = configColors?.['footerBorderTop'];

        if (footerBg && footerBg !== 'transparent') {
            const contrast = pickContrastingTextColor(footerBg);
            this.isDarkBg = contrast === 'white';
            const contrastLinkColor = this.isDarkBg ? '#ffffff' : '#000000';
            this.footerLinkColor = configColors?.['footerLinkColor'] || contrastLinkColor;
            this.footerTextColor = contrastLinkColor;
        } else {
            this.isDarkBg = false;
            this.footerLinkColor = configColors?.['footerLinkColor'];
            this.footerTextColor = undefined;
        }
    }
}
